import { useState } from 'react'

import dayjs from 'dayjs'

import { useVisitorSummaryApi } from '@/common/api/modules/customer'
import { VisitorSegment } from '@/common/api/params/customer'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'

import { useVisitorInsight } from '@/components/container/modules/recommend/customer/summary/useVisitorInsight'
import VisitorInsightSection from '@/components/container/modules/recommend/customer/summary/VisitorInsightSection'

/**
 * 訪問者サマリ（2026-10-04 追加・SMART_HUB_DEV-127 / openspec: visitor-summary-insight）。
 *
 * 訪問者を1人ずつ開かずに、到達率と、それが流入・デバイスでどう違うかを見る。
 * **会員／ゲスト・配信接触の到達率比較はわざと出さない。**会員IDは購入時に紐づき、
 * 配信の未接触はほぼ離脱者なので、並べると差が効果に見えてしまう。
 */

const PERIODS = [30, 90] as const

const REFERRER_LABEL: Record<string, string> = {
  ad: '広告',
  direct: '直接（ブックマーク・URL入力など）',
  organic: '自然検索',
  internal: 'サイト内',
  social: 'SNS',
  other: 'その他の外部サイト',
  unknown: '不明'
}

const DEVICE_LABEL: Record<string, string> = {
  mobile: 'スマートフォン',
  pc: 'PC',
  tablet: 'タブレット',
  unknown: '不明'
}

/** 0〜1 の率を % で出す。分母が 0（null）は「—」 */
const pct = (v: number | null) => (v === null ? '—' : `${(v * 100).toFixed(2)}%`)
const num = (v: number) => v.toLocaleString()

const FunnelStep = ({
  label,
  count,
  rate,
  stepLabel,
  stepRate
}: {
  label: string
  count: number
  rate: number | null
  stepLabel?: string
  stepRate?: number | null
}) => (
  <div className='border-border-input flex min-w-[150px] flex-1 flex-col gap-1 rounded-lg border p-3'>
    <Text size={12} color={TextColor.SECONDARY}>
      {label}
    </Text>
    <Text size={20} weight='bold'>
      {num(count)}人
    </Text>
    <Text size={12}>訪問者の {pct(rate)}</Text>
    {stepLabel && (
      <Text size={10} color={TextColor.TERTIARY}>
        {stepLabel} {pct(stepRate ?? null)}
      </Text>
    )}
  </div>
)

const SegmentTable = ({
  title,
  note,
  rows,
  labels
}: {
  title: string
  note: string
  rows: VisitorSegment[]
  labels: Record<string, string>
}) => (
  <div className='flex flex-col gap-1'>
    <div className='flex flex-wrap items-baseline gap-2'>
      <Text size={13} weight='bold'>
        {title}
      </Text>
      <Text size={10} color={TextColor.TERTIARY}>
        {note}
      </Text>
    </div>
    <Table>
      <thead>
        <TableRow head>
          {['区分', '訪問者', '構成比', '商品閲覧', 'カート到達', '注文到達', '閲覧→カート', 'カート→注文'].map(
            (h) => (
              <TableHeadItem key={h}>
                <Text size={12} nowrap>
                  {h}
                </Text>
              </TableHeadItem>
            )
          )}
        </TableRow>
      </thead>
      <tbody>
        {rows.map((r) => (
          <TableRow key={r.key}>
            <TableRowItem>
              <div className='flex items-center gap-2'>
                <Text size={12} nowrap>
                  {labels[r.key] ?? r.key}
                </Text>
                {r.small && (
                  <Text size={10} color={TextColor.CAUTION} nowrap>
                    参考
                  </Text>
                )}
              </div>
            </TableRowItem>
            {[
              num(r.visitors),
              pct(r.share),
              pct(r.product_view_rate),
              pct(r.cart_rate),
              pct(r.order_rate),
              pct(r.cart_per_product_view),
              pct(r.order_per_cart)
            ].map((v, i) => (
              <TableRowItem key={i}>
                <Text size={12} color={r.small ? TextColor.TERTIARY : undefined} nowrap>
                  {v}
                </Text>
              </TableRowItem>
            ))}
          </TableRow>
        ))}
      </tbody>
    </Table>
  </div>
)

const VisitorSummaryPanel = ({ siteId }: { siteId: number }) => {
  const [days, setDays] = useState<number>(30)
  const { data, isLoading } = useVisitorSummaryApi({ site_id: siteId, days })
  const insight = useVisitorInsight(siteId, days)

  return (
    <Area>
      <Heading
        subText='訪問者を1人ずつ開かずに、どこで取りこぼしているか、流入・デバイスでどう違うかを見ます。'
        rightNode={
          <div className='flex gap-2'>
            {PERIODS.map((d) => (
              <Button
                key={d}
                color={days === d ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                onClick={() => setDays(d)}
              >
                直近{d}日
              </Button>
            ))}
          </div>
        }
      >
        訪問者サマリ
      </Heading>
      <AreaContent>
        {isLoading || !data ? (
          <Text size={12} color={TextColor.SECONDARY}>
            集計しています…
          </Text>
        ) : data.population.visitors === 0 ? (
          <Text size={12} color={TextColor.SECONDARY}>
            この期間の訪問者はいません。
          </Text>
        ) : (
          <div className='flex flex-col gap-5'>
            <div className='flex flex-col gap-2'>
              <Text size={12} color={TextColor.SECONDARY}>
                最終訪問が直近{data.days}日の訪問者 {num(data.population.visitors)}人（会員{' '}
                {num(data.population.members)}人・ゲスト {num(data.population.guests)}人）
              </Text>
              <div className='flex flex-wrap gap-3'>
                <FunnelStep label='訪問者' count={data.funnel.visitors} rate={1} />
                <FunnelStep
                  label='商品を見た'
                  count={data.funnel.product_view}
                  rate={data.funnel.product_view_rate}
                />
                <FunnelStep
                  label='カートに入れた'
                  count={data.funnel.cart}
                  rate={data.funnel.cart_rate}
                  stepLabel='商品を見た人のうち'
                  stepRate={data.funnel.cart_per_product_view}
                />
                <FunnelStep
                  label='注文した'
                  count={data.funnel.order}
                  rate={data.funnel.order_rate}
                  stepLabel='カートに入れた人のうち'
                  stepRate={data.funnel.order_per_cart}
                />
              </div>
            </div>

            <SegmentTable
              title='流入別'
              note='初回の入口で分けています'
              rows={data.by_referrer}
              labels={REFERRER_LABEL}
            />
            <SegmentTable
              title='デバイス別'
              note='最後に訪問したときのデバイスで分けています'
              rows={data.by_device}
              labels={DEVICE_LABEL}
            />

            <div className='flex flex-col gap-0.5'>
              <Text size={10} color={TextColor.TERTIARY}>
                ・人数は訪問者（Cookie）単位です。注文は「注文に到達した訪問者」の人数で、注文件数ではありません。
              </Text>
              <Text size={10} color={TextColor.TERTIARY}>
                ・CSV取込の受注も紐づくため、カート操作が計測されていない注文到達者がいます。「カート→注文」はカートに入れた人のうち注文した割合です。
              </Text>
              <Text size={10} color={TextColor.TERTIARY}>
                ・「参考」は訪問者が{data.min_segment}人未満の区分で、数人の差で率が大きく動きます。
              </Text>
              <Text size={10} color={TextColor.TERTIARY}>
                ・会員とゲストの到達率は比べていません。会員番号は購入時に紐づくため、差が「会員化の効果」に見えてしまうためです。
              </Text>
              {data.counters_synced_at && (
                <Text size={10} color={TextColor.TERTIARY}>
                  ・カウンタの最終再集計：{dayjs(data.counters_synced_at).format('YYYY/MM/DD HH:mm')}
                </Text>
              )}
            </div>

            <VisitorInsightSection state={insight} />
          </div>
        )}
      </AreaContent>
    </Area>
  )
}

export default VisitorSummaryPanel
